// External Imports
import React, { memo, useContext, useEffect, useMemo, useState, useCallback, useRef } from 'react'
import { AiOutlineClose } from '@react-icons/all-files/ai/AiOutlineClose'
import { useDispatch, useSelector } from 'react-redux'
import { Link, useNavigate, useLocation } from 'react-router-dom'

// Internal Imports
import AddressOptions from 'components/addressOptions'
import Button from 'components/common/button'
import { useDialog, Container, useDialogSignInOptions } from 'components/common/modal/Modal'
import SearchBar from 'components/searchbar'
import SignIn, { SignInForm } from 'components/signIn'
import { RouteConstants } from 'config/routes/routeConstants'
import { LanguageContext } from 'context/LanguageContext'
import { LayoutContext } from 'context/LayoutContext'
import { trackDisplayType } from 'middleware/helper'
import { refreshApp } from 'modules/authentication/actions'
import { setCountryForGlobalAssortment, setAddress } from 'modules/location/actions'
import { getCurrentOrdersList, setIsReOrder } from 'modules/orders/actions'
import { util } from 'utils'
import Util from 'utils/Util'
import { isDeliveryMode, getCurrentCountry, isEmpty, orderTypes, camelCase } from 'utils/appUtils'
import constants from 'utils/constants'
import { getUtmSearch } from 'utils/parse'
import { getCDNURL } from 'network/api/networkSettings'
// import SelectAddress from 'components/selectAddress'
import CustomBasketIcon from '../customBasketIcon'
import menuIcon from '../../../assets/images/menu.svg'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'

import LanguageSelector from '../language/Selector'
import { ModeSwitchPopUp } from '../modeSwitchPopUp'
import { saveCateringAddress, updateCateringAddress, setCateringSchedule, getCateringDataFromStorage } from 'modules/catering/actions'
import ResponsiveDateTimePickers from '../dateTimePicker'

// Extend dayjs with timezone support
dayjs.extend(utc)
dayjs.extend(timezone)

const { DEFAULT_TIMEZONE } = constants;

const DesktopHeader = memo(() => {
    const { rtl, strings } = useContext(LanguageContext)
    const applogo = getCDNURL() + 'assets/images/applogo.png'

    const dispatch = useDispatch()
    const { pathname, search } = useLocation()
    const locationData = useLocation()
    const navigate = useNavigate()
    const { isSignedIn } = useMemo(() => util.getJWTDetails())
    const currentAddress = useSelector(state => state.location.getIn(['selectedAddress']))
    // const [cateringData, setCateringData] = useState(getCateringDataFromStorage())
    const cateringData = useSelector(state => state.catering)
    const [modeSwitchOpen, setModeSwitchOpen] = useState(false)
    const [targetMode, setTargetMode] = useState(null)
    const isAddressValid = useSelector(state => state.location.get('uspsValidatedAddressSuccess'))
    const isLoading = useSelector(state => state.languageChange.get('isLoading'))
    const isAddressDeliverable = useSelector(state => state.location.get('isAddressDeliverable'))
    const activeOrdersCount = useSelector(state => state.orders.get('activeOrdersCount'))
    const activeOrder = useSelector(state => state.orders.get('currentOrders')[0])
    const isCateringMode = useSelector(state => state.appSettings.get('isCateringMode'))
    const userProfile = useSelector(state => state.user.get('userProfile'))
    const preorderEnabledForGuestUser = useSelector(state =>
        state.configuration.get('is_preorder_enabled_for_guest_user')
    )
    const preOrderPilotUsers = useSelector(state => state.configuration.get('preOrderPilotUsers'))

    const isGuestPreorderEnabled = preorderEnabledForGuestUser === true

    const pilotUsersEnabled =
        preOrderPilotUsers?.get?.('enabled') === true || preOrderPilotUsers?.enabled === true
    const pilotUserValuesRaw = preOrderPilotUsers?.get?.('value') ?? preOrderPilotUsers?.value ?? []
    const pilotUserValues =
        typeof pilotUserValuesRaw?.toJS === 'function' ? pilotUserValuesRaw.toJS() : pilotUserValuesRaw
    const loyaltyId = userProfile?.get?.('loyalty_id') ?? userProfile?.loyalty_id
    const canSignedInUserSeeCateringTab =
        pilotUsersEnabled &&
        !!loyaltyId &&
        (pilotUserValues.length === 0 || pilotUserValues.map(String).includes(String(loyaltyId)))

    const showCateringTab = isSignedIn ? canSignedInUserSeeCateringTab : isGuestPreorderEnabled
    const currentPath = pathname
    const routes = currentPath.split('/')
    const redirectHome = routes.length > 2 && (routes[1] === 'shop' || 'home')

    const displayAddress = currentAddress
        ? currentAddress.get('type') + ': ' + currentAddress.get('formatted_address')
        : isLoading
          ? 'Enter your address'
          : strings.HEADER_ENTER_ADDRESS

    const displayAddressHead = currentAddress?.get('formatted_address')
    const cateringAddressHead = cateringData?.address?.formatted_address
    // Get store timezone from catering store, fallback to default
    const storeTimezone = cateringData?.store?.time_zone || DEFAULT_TIMEZONE
    const displayAddressSub1 = currentAddress ? camelCase(currentAddress.get('type')) + ' ' + strings.from.toLowerCase() : strings.pickupOrDelivery
    const displayAddressSub2 = currentAddress ? currentAddress.get('type') === orderTypes.PICKUP ? strings.at : strings.etaLabel : ''
    
    // Catering-specific display variables
    const { on, hide, show, getToggleProps, getContainerProps } = useDialog()
    const [showDateTimePicker, setShowDateTimePicker] = useState(false)
    const cateringSchedule = cateringData?.schedule
    const cateringDateTime = cateringSchedule?.date && cateringSchedule?.time 
        ? `${cateringSchedule.date} ${cateringSchedule.time}` 
        : null
    
    const prevAddressRef = useRef(null)
    const isInitialLoad = useRef(true)
    const prevIsCateringModeRef = useRef(isCateringMode)
    const {
        showSignIn,
        hideSignInOptions,
        showSignInOptions,
        getToggleSignInProps,
        getSignInContainerProps,
    } = useDialogSignInOptions()

    const { openPanel, signInPanel, panelOpen, closePanel } = useContext(LayoutContext)
    const basketResponse = useSelector(state => state.basket.get('basketResponse'))
    const patchInProgress = useSelector(state => state.basket.get('cartPatchInProgress'))
    const getCartInProgress = useSelector(state => state.basket.get('getCartInProgress'))
    const basketItems = useSelector(state => state.basket.getIn(['basketItems']))
    const totalBasketCount = util.getTotalItems(basketItems)
    const basketCount = totalBasketCount > 0 ? totalBasketCount : ''
    const currentStore = useSelector(state => state.location.getIn(['selectedStore']))

    const location = useSelector(state => state.location)
    const user = useSelector(state => state.user)
    const globalAssortmentCountry =
        useSelector(state => state.location.getIn(['countryForGlobalAssortment'])) ||
        getCurrentCountry(location, user)
    const ETAPrefix = isDeliveryMode()
        ? isLoading
            ? 'ETA:'
            : strings.etaLabel + ' '
        : ' '
    const ETAPresent = basketResponse && basketResponse.toJS().isETAPresent
    const ETAMessage = ETAPresent
        ?  basketResponse.toJS().ETA
        : !basketResponse || !currentAddress || !currentStore
          ? ''
          : isDeliveryMode()
            ? isLoading
                ? 'Drivers not available'
                : strings.driversNotAvailable
            : ''
    const estimatedTime =
        patchInProgress || getCartInProgress
            ? isLoading
                ? 'Updating...'
                : strings.updating
            : ETAMessage
    const newUserFaceBook = useSelector(state => state.authentication.getIn(['newUserFaceBook']))
    const [state_NewUserFaceBook, setNewUserFaceBook] = useState(
        useSelector(state => state.authentication.getIn(['newUserFaceBook']))
    )
    const promotions = useSelector(state => state.products.homePageContent['promotions'])
    const noDeals = !promotions || promotions.length === 0
    const itemsAddedToCart = basketItems ? basketItems.toJS() : []

    // Note: DateTimePicker is now triggered only when user explicitly clicks "Select Store"
    // in ConfirmStore.js, not on address changes

    // Restore catering data from session storage when switching to catering mode
    useEffect(() => {
        const wasCateringMode = prevIsCateringModeRef.current
        prevIsCateringModeRef.current = isCateringMode

        if (isCateringMode) {
            // Entering catering mode - save current shop address and restore catering data
            const currentShopAddress = currentAddress?.toJS?.() || currentAddress
            if (currentShopAddress) {
                sessionStorage.setItem('shop_address_backup', JSON.stringify(currentShopAddress))
            }
            
            const freshData = getCateringDataFromStorage();
            const { address, schedule, store } = freshData
            dispatch(updateCateringAddress(address, schedule, store))
            return
        }

        if (!wasCateringMode) return

        // Leaving catering mode - restore shop address if it exists
        const shopAddressBackup = sessionStorage.getItem('shop_address_backup')
        if (!shopAddressBackup) return

        try {
            const shopAddress = JSON.parse(shopAddressBackup)
            dispatch(setAddress(shopAddress))
        } catch (error) {
            console.error('Error restoring shop address:', error)
        } finally {
            sessionStorage.removeItem('shop_address_backup')
        }
    }, [isCateringMode]);


    // Handle DateTimePicker changes
    const handleDateTimeChange = (dateTime) => {
        const schedule = {
            date: dayjs(dateTime).format('YYYY-MM-DD'),
            time: dayjs(dateTime).format('HH:mm'),
            isValid: true,
            updatedAt: Date.now()
        };
        
        dispatch(setCateringSchedule(schedule));
        
        setShowDateTimePicker(false);
    };
    useEffect(() => {
        if (
            newUserFaceBook &&
            newUserFaceBook.toJS().first_name &&
            newUserFaceBook.toJS().last_name
        ) {
            setNewUserFaceBook(newUserFaceBook.toJS())
            show()
        }
    }, [newUserFaceBook])

    useEffect(() => {
        if (panelOpen && signInPanel) {
            closePanel()
        }
    }, [isSignedIn])

    useEffect(() => {
        const interval = setInterval(() => {
            dispatch(getCurrentOrdersList())
        }, constants.appRefreshTime)
        return () => clearInterval(interval)
    }, [])

    useEffect(() => {
        dispatch(refreshApp())
        trackDisplayType('Desktop Browser')
    }, [])

    useEffect(() => {
        if (activeOrdersCount === 1) {
            dispatch(getCurrentOrdersList())
        }
    }, [activeOrdersCount])

    const handleSelectCountry = e => {
        dispatch(setCountryForGlobalAssortment(e.target.value))
        if (redirectHome) navigate('/home')
    }

    const handleModeSwitch = useCallback((e, mode) => {
        e.preventDefault()
        dispatch(setIsReOrder(false))

        const hasItems = itemsAddedToCart.length > 0
        const hasPreOrderItems = Util.checkForPreOrderItems(itemsAddedToCart) // All items are platter items
        
        if (hasItems) {
            // Show warning only when switching between incompatible modes
            const shouldShowWarning = 
                // Has platter items and switching to shop/home
                (hasPreOrderItems && (mode === 'shop' || mode === 'home')) ||
                // Has only non-platter items and switching to catering
                (!hasPreOrderItems && mode === 'catering')

            if (shouldShowWarning) {
                setTargetMode(mode)
                setModeSwitchOpen(true)
                return
            }
        }

        // Direct navigation for all other cases
        const targetPath = mode === 'catering' ? '/catering' : mode === 'home' ? '/home' : '/shop'
        navigate(`${targetPath}${getUtmSearch(search)}`)
    }, [pathname, navigate, search, itemsAddedToCart])

    const handleLogoClick = (e) => {
        e.preventDefault()
        if(Util.checkForPreOrderItems(itemsAddedToCart)){
            setTargetMode('home')
            setModeSwitchOpen(true)
        }
        else{
            navigate(`/home${getUtmSearch(search)}`)
        }
    }

    return (
        <div className="app-header">
            <header className="header__desktop">
                <div className="header__desktop__left">
                    <button onClick={handleLogoClick}>
                        <img src={applogo} alt={strings.Logo} />
                    </button>
                    {isCateringMode ? (
                        <button
                            className={`header__desktop__address header__desktop__address__catering`}
                            {...getToggleProps()}
                            aria-label="delivery/pickup address"
                        >
                            {cateringData?.address ? (
                                <div className='header__desktop__address__store_details'>
                                    <div className="header__desktop__address__first_line">
                                        <span
                                            className="header__desktop__address__non_clickable"
                                            aria-label="Pickup from"
                                        >
                                           {strings.pickupFrom}&nbsp;
                                        </span>
                                        <span
                                            className="header__desktop__address__clickable"
                                            tabIndex="0"
                                            aria-label={cateringAddressHead}
                                        >
                                            {cateringAddressHead}
                                        </span>
                                    </div>
                                    {cateringData?.schedule && (
                                        <div className="header__desktop__address__second_line">
                                            <span
                                                className="header__desktop__address__non_clickable"
                                                aria-label="at"
                                            >
                                                at&nbsp;
                                            </span>
                                            <span
                                                className="header__desktop__address__clickable"
                                                tabIndex="0"
                                                aria-label={dayjs(cateringData?.schedule?.date + ' ' + cateringData?.schedule?.time).format('MMM DD, h:mm A')}
                                                onClick={(e) => {
                                                    e.stopPropagation(); // Prevent address modal from opening
                                                    setShowDateTimePicker(true);
                                                }}
                                                style={{ cursor: 'pointer' }}
                                            >
                                                {dayjs(cateringData?.schedule?.date + ' ' + cateringData?.schedule?.time).format('MMM DD, h:mm A')}
                                            </span>
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <div className='header__desktop__address__store_details'>
                                    <span
                                        className="header__desktop__address__store_details_subHead"
                                        tabIndex="0"
                                        aria-label={''}
                                    >
                                        {strings.startCateringOrder}
                                    </span>
                                    <span className={`header__desktop__address__store_details_head`}>
                                        {strings.findAStore}
                                    </span>
                                </div>
                            )}
                        </button>
                    ) : (
                        <button
                            className={`header__desktop__address`}
                            {...getToggleProps()}
                            aria-label="delivery/pickup address"
                        >
                            <div className='header__desktop__address__store_details'>
                                <span
                                    className="header__desktop__address__store_details_subHead"
                                    tabIndex="0"
                                    aria-label={displayAddressSub1}
                                >
                                    {displayAddressSub1}
                                </span>
                                <span className={`header__desktop__address__store_details_head`}>
                                    {displayAddressHead}
                                </span>
                            </div>

                            {on && !state_NewUserFaceBook && (
                                <Container {...getContainerProps()}>
                                    <div className="signinform">
                                        <Button
                                            {...getToggleProps()}
                                            aria-label="close modal"
                                            className="closeModal"
                                        >
                                            <AiOutlineClose className="close" size="2.5rem" />
                                        </Button>
                                        <AddressOptions hide={hide} show={show} />
                                        {/* <SelectAddress hide={hide} show={show} /> */}
                                    </div>
                                </Container>
                            )}

                            <div className='header__desktop__address__eta_details'>
                                {displayAddressSub2 && <span
                                    className="header__desktop__address__eta_details_subHead"
                                    tabIndex="0"
                                    aria-label={displayAddressSub2}
                                >
                                    {displayAddressSub2}
                                </span>}
                                <span className={`header__desktop__address__eta_details_head`}>
                                    {currentAddress ? estimatedTime : strings.findAStore}
                                </span>
                            </div>
                        </button>
                    )}
                </div>

                {/* Address modal for catering address */}
                {isCateringMode && on && (
                    <Container {...getContainerProps()}>
                        <div className="signinform">
                            <Button
                                {...getToggleProps()}
                                aria-label="close modal"
                                className="closeModal"
                            >
                                <AiOutlineClose className="close" size="2.5rem" />
                            </Button>
                            <AddressOptions 
                                hide={hide} 
                                show={show}
                                setShowDateTimePicker={setShowDateTimePicker}
                            />
                            {/* <SelectAddress hide={hide} show={show} /> */}
                        </div>
                    </Container>
                )}

                {/* DateTimePicker modal for catering */}
                {isCateringMode && cateringData?.address && (
                    <ResponsiveDateTimePickers 
                        open={showDateTimePicker}
                        onClose={() => setShowDateTimePicker(false)}
                        onDateTimeChange={handleDateTimeChange}
                        initialValue={cateringDateTime}
                        order={locationData.state}
                        storeTimezone={storeTimezone}
                    />
                )}

                {state_NewUserFaceBook && state_NewUserFaceBook.first_name && (
                    <div>
                        {on && (
                            <Container {...getContainerProps()}>
                                <div className="registerform">
                                    <Button {...getToggleProps()} aria-label="close modal">
                                        <AiOutlineClose className="close" size="2.5rem" />
                                    </Button>
                                    <AddressOptions hide={hide} show={show} registration />
                                    {/* <SelectAddress hide={hide} show={show} registration /> */}
                                </div>
                            </Container>
                        )}
                    </div>
                )}

                <div className="header__desktop__center">
                    <SearchBar className="searchbar" panelOpen={panelOpen} />
                    <nav className="links">
                        <ul className={`links-wrapper ${!isSignedIn ? 'links-wrapper-guest' : ''}`}>
                            {!noDeals && (
                                <li className={ `links-wrapper-text ${pathname.indexOf('/deals') === 0 ? 'active' : ''}`} >
                                    <Link to={`/deals${getUtmSearch(search)}`} className='links-wrapper-text-link' >
                                        {isLoading ? 'DEALS' : strings.SECTION_DEALS_TITTLE}
                                    </Link>
                                </li>
                            )}
                            <li className={ `links-wrapper-text ${pathname.indexOf('/shop') === 0 ? 'active' : ''}`}>
                                <button 
                                    className='links-wrapper-text-link' 
                                    onClick={(e) => handleModeSwitch(e, 'shop')}
                                >
                                    {isLoading ? 'SHOP' : strings.SECTION_SHOP_TITTLE}
                                </button>
                            </li>
                            {showCateringTab && (
                                <li className={ `links-wrapper-text ${pathname.indexOf('/catering') === 0 ? 'active' : ''}`}>
                                    <button 
                                        className='links-wrapper-text-link' 
                                        onClick={(e) => handleModeSwitch(e, 'catering')}
                                    >
                                        {isLoading ? 'CATERING' : strings.catering}
                                    </button>
                                </li>
                            )}
                        </ul>
                    </nav>
                </div>

                <div className='header__desktop__right'>
                    <LanguageSelector />
                    {isSignedIn ? (
                        <li className={`links-wrapper-orders ${panelOpen ? 'links-wrapper-orders-panel-open' : ''}`}>
                            <button
                                onClick={() => {
                                    openPanel('orderPanel')
                                }}
                                aria-label="Orders Link"
                            >
                                {' '}
                                {isLoading ? 'ORDERS' : strings.SECTION_ORDERS_TITTLE}
                            </button>
                        </li>
                    ) : ''}
                    <CustomBasketIcon count={basketCount} />

                    {isSignedIn ? (
                        !panelOpen && (<div className={`accountInfo`}>
                            <img
                                className="profile-image"
                                onClick={() => openPanel('accountPanel')}
                                aria-label={'profile icon, enter to open your account information'}
                                src={menuIcon}
                            />
                        </div>)
                    ) : (
                        <div className={'profile'}>
                            <SignIn />
                        </div>
                    )}
                </div>
                
            </header>
            {isAddressValid && isEmpty(currentAddress) && isAddressDeliverable === false && (
                <div className="address-feedback">
                    {strings.outOfServiceArea} <a {...getToggleProps()}>{strings.hereLabel}</a>.
                </div>
            )}
            {pathname.indexOf(RouteConstants.OrderStatus) === -1 && activeOrdersCount > 0 ? (
                <div
                    className="order-feedback"
                    tabIndex="0"
                    aria-label={`${strings.activeOrderCount1} ${activeOrdersCount} ${strings.activeOrderCount3}`}
                >
                    {activeOrdersCount === 1
                        ? `${strings.activeOrderCount1} ${activeOrdersCount} ${strings.activeOrderCount2}`
                        : `${strings.activeOrderCount1} ${activeOrdersCount} ${strings.activeOrderCount3}`}{' '}
                    {activeOrdersCount === 1 ? (
                        <Link
                            to={`${RouteConstants.OrderStatus}${getUtmSearch(search)}`}
                            state={{ order: activeOrder }}
                            className="hereLink"
                        >
                            {strings.hereLabel}
                        </Link>
                    ) : (
                        <button
                            className={'here'}
                            onClick={() => openPanel('orderPanel')}
                            tabIndex="0"
                        >
                            {strings.hereLabel}
                        </button>
                    )}
                    .
                </div>
            ) : null}
            <ModeSwitchPopUp
                open={modeSwitchOpen}
                onClose={() => setModeSwitchOpen(false)}
                targetMode={targetMode}
                search={search}
            />
        </div>
    )
})
export default DesktopHeader
